import {
  codeSourceLabel,
  hasTrainDataset,
  isDirectConfigEdit,
  isExperimentComplete,
  isInitComplete,
  isRecipeComplete,
  isResourcesComplete,
  learningCoreRef,
  reservedInitOverrideKeys,
  type SubmitFormState,
} from "./submitWizardForm";

// 구성 스텝(②) 내비의 섹션 모델. 판정을 새로 만들지 않고 submitWizardForm 의 제출 게이트
// 술어를 그대로 읽는다. 그래서 내비가 초록인데 '다음'이 잠기는 어긋남이 생길 수 없다.
//
// 한 섹션이 한 화면을 채운다. 함께 정하는 항목끼리 묶는다. 브랜치 및 config 는 레시피 출처와
// 그 위에 추가하는 값(mux·override·옵션)을 함께 다룬다.
//
// **학습 방식과 데이터셋은 한 섹션에 함께 둔다**(사용자 확정). 시작점이 먼저이고, 그 아래에 학습
// 데이터가 있다. 두 축은 서로 독립이라 서로를 좁히지 않지만, 정하는 순서가 이 순서다. 라벨은 축마다
// 따로 있다(본문의 두 필드). 그래서 "단일 데이터셋"과 "finetune" 을 한 선택으로 오해하지 않는다
// (ZERG-294).
//
// 메뉴 이름은 한국어이고, 그 안의 필드 이름은 learning-core 변수명을 그대로 쓴다. 무엇을 고르는
// 자리인지는 한국어로 표시하고, 실제로 바꾸는 키는 config 본문에서 찾을 이름으로 표시한다.
//
// **Isaac Evaluation 은 조건부 섹션이다.**
//   - evaluation 권한이 없으면 목록에서 제거한다. Evaluation 메뉴처럼 기능이 있다는 사실을 숨긴다.
//   - 권한이 있고 담은 데이터셋 중 하나라도 시뮬레이션이면, 브랜치 및 config 와 리소스 사이에서
//     고를 수 있다.
//   - 권한이 있지만 시뮬레이션 데이터셋이 없으면 같은 자리에 두되 고를 수 없다. 항목이 사유를
//     가지고 내비가 툴팁으로 표시한다. 권한이 있는 사용자에게는 항목을 빼지 않고 이유를 알린다.
// 숨김/잠금 판정은 컨트롤러가 정한다(`useSubmitWizardController`). 아래 상수는 전체 순서와 타입의
// 소스이고, 고를 수 있는 목록은 `selectableConfigureSections` 가 정한다.

export const CONFIGURE_SECTIONS = [
  "experiment",
  "datasets",
  "config",
  "evaluation",
  "resources",
] as const;

export type ConfigureSection = (typeof CONFIGURE_SECTIONS)[number];

// ok = 제출 게이트를 통과한 상태 / incomplete = 필수 입력이 비었다 / warning = 값은 있지만
// 이대로 제출하면 BE 가 거부한다.
export type ConfigureSectionStatus = "ok" | "incomplete" | "warning";

// 내비 요약이 쓸 수 있는 줄 수 상한. 항목 높이가 서로 다르면 목록을 현황판으로 읽기 어렵다.
export const SECTION_SUMMARY_MAX_LINES = 3;

// 요약 한 줄은 축 이름(name)과 현재 값(value)이다. 값만 표시하면 `실험 A` 아래 `-` 가 어느
// 항목의 빈 값인지 알 수 없다. 그래서 축 이름을 값과 함께 가진다.
export type SectionSummaryAxis = { name: string; value: string };

export type ConfigureSectionEntry = {
  section: ConfigureSection;
  label: string;
  // 내비에서 펼치지 않아도 현재 값을 읽을 수 있게 하는 요약. 축마다 한 줄씩 쌓고
  // `SECTION_SUMMARY_MAX_LINES` 줄에서 자른다.
  summaryAxes: SectionSummaryAxis[];
  status: ConfigureSectionStatus;
  // 고를 수 없는 항목의 사유(내비가 툴팁으로 표시한다). null 이면 고를 수 있다.
  disabledReason: string | null;
};

// Isaac Evaluation 섹션 요약에 필요한 값. 폼이 아니라 컨트롤러가 가진 값(토글·이미지
// 메타·plan)이라 값 객체로 받는다. 이 모듈은 훅 타입을 모른다.
export type EvaluationSectionInput = {
  // 시뮬레이션 데이터셋이 있을 때만 유효한, 마스킹된 토글 값.
  enabled: boolean;
  // 이미지·TASK·SEED 가 모두 있어서 제출할 수 있는지(컨트롤러 plan.ready).
  ready: boolean;
  imageTag: string;
  task: string;
};

// 잠긴 Isaac Evaluation 섹션. 사유(시뮬레이션 부재)는 컨트롤러가 정해서 전달하고,
// 내비가 툴팁으로 표시한다. 사유 없는 잠김은 타입으로 표현할 수 없다.
export type LockedEvaluationSection = {
  lockedReason: string;
};

// evaluation 이 `null` 이면 섹션을 목록에서 제거한다. evaluation 권한이 없는 사용자에게는
// 기능이 있다는 사실을 숨긴다(Evaluation 메뉴와 같은 규칙). 잠금+툴팁은 권한이 있는 사용자에게
// 고를 수 없는 이유(시뮬레이션 부재)를 알릴 때만 쓴다.
export function buildConfigureSections(
  form: SubmitFormState,
  evaluation: EvaluationSectionInput | LockedEvaluationSection | null,
): ConfigureSectionEntry[] {
  const entries: ConfigureSectionEntry[] = [
    { section: "experiment", label: "실험", disabledReason: null, ...experimentState(form) },
    {
      section: "datasets",
      label: "Training 방식 · Dataset",
      disabledReason: null,
      ...datasetsState(form),
    },
    { section: "config", label: "Branch 및 config", disabledReason: null, ...configState(form) },
  ];
  if (evaluation !== null) {
    // 잠금 여부를 한 번만 판정해서 사유와 요약 상태가 어긋날 수 없게 한다.
    entries.push({
      section: "evaluation",
      label: "Isaac Evaluation",
      ...("lockedReason" in evaluation
        ? { disabledReason: evaluation.lockedReason, ...LOCKED_EVALUATION_STATE }
        : { disabledReason: null, ...evaluationState(evaluation) }),
    });
  }
  entries.push({
    section: "resources",
    label: "리소스",
    disabledReason: null,
    ...resourcesState(form),
  });
  return entries;
}

const SECTIONS_WITHOUT_EVALUATION: readonly ConfigureSection[] = CONFIGURE_SECTIONS.filter(
  (section) => section !== "evaluation",
);

// 지금 고를 수 있는 섹션 목록. `buildConfigureSections` 가 주는 항목 중 잠기지 않은 항목의 순서와
// 같다. 값이 두 개뿐이라 참조가 안정적이고, 이동·스냅 판정이 폼 갱신마다 새 배열을 받지 않는다.
export function selectableConfigureSections(hasEvaluation: boolean): readonly ConfigureSection[] {
  return hasEvaluation ? CONFIGURE_SECTIONS : SECTIONS_WITHOUT_EVALUATION;
}

// 고를 수 없는 섹션(시뮬레이션 데이터셋을 빼서 Isaac Evaluation 이 잠긴 경우)을 화면이 그릴 수 있는
// 섹션으로 되돌린다. 전체 순서에서 바로 앞의 고를 수 있는 섹션을 쓰고, 없으면 첫 섹션을 쓴다. 이웃
// 정보는 `CONFIGURE_SECTIONS` 하나가 가진다.
export function resolveSelectableConfigureSection(
  section: ConfigureSection,
  sections: readonly ConfigureSection[],
): ConfigureSection {
  if (sections.includes(section)) {
    return section;
  }
  for (let at = CONFIGURE_SECTIONS.indexOf(section) - 1; at >= 0; at -= 1) {
    if (sections.includes(CONFIGURE_SECTIONS[at])) {
      return CONFIGURE_SECTIONS[at];
    }
  }
  return sections[0];
}

// 다음/이전 섹션. 지금 고를 수 있는 목록 안에서만 이동한다(잠긴 Isaac Evaluation 을 건너뛴다).
// 끝에서는 멈춘다. 순환하면 마지막에서 한 번 더 눌렀을 때 처음으로 이동해서, 사용자가 어디까지
// 봤는지 알 수 없다.
export function adjacentConfigureSection(
  section: ConfigureSection,
  step: 1 | -1,
  sections: readonly ConfigureSection[],
): ConfigureSection | null {
  const at = sections.indexOf(section) + step;
  return sections[at] ?? null;
}

type SectionState = { summaryAxes: SectionSummaryAxis[]; status: ConfigureSectionStatus };

// 아직 채우지 않은 고정 축의 자리 표시. 줄을 빼면 그 축이 있는지도 보이지 않고 항목 높이도
// 값에 따라 달라진다. 그래서 자리를 유지하고 비었다는 사실만 표시한다.
export const EMPTY_AXIS = "-";

// 축 목록을 요약 줄로 만든다. value 가 null 인 선택 축(값이 있을 때만 자리를 받는 축)을
// 제거하고 상한에서 자른다.
function summaryOf(axes: readonly { name: string; value: string | null }[]): SectionSummaryAxis[] {
  return axes
    .filter((axis): axis is SectionSummaryAxis => axis.value !== null && axis.value !== "")
    .slice(0, SECTION_SUMMARY_MAX_LINES);
}

// 실험 이름과 세션 이름은 층이 다른 값이라 한 줄씩 쓴다. 둘 다 이 섹션의 고정 축이라 비어도
// 자리를 유지한다. 워크스페이스는 요약에 없다. admin 이 책임지는 개념이고 화면에서 고르지도
// 않는다(docs/rules.md §15).
function experimentState(form: SubmitFormState): SectionState {
  const displayName = form.displayName.trim();
  return {
    summaryAxes: summaryOf([
      {
        name: "실험 이름",
        value: form.experimentId === "" ? EMPTY_AXIS : (form.experimentName ?? form.experimentId),
      },
      { name: "Session 이름", value: displayName === "" ? EMPTY_AXIS : displayName },
    ]),
    status: isExperimentComplete(form) ? "ok" : "incomplete",
  };
}

// 요약은 본문과 같은 순서로 쌓는다. 시작점(학습 방식)이 먼저이고, 담은 Dataset 이 그 아래다.
//
// Dataset 은 이름으로 표시한다. 개수만으로는 학습 데이터를 알 수 없다. 축 하나에 여러 이름이
// 들어가므로 한 줄에 이어 쓰고, 넘치면 남은 개수로 줄인다. 이름만 자르면 개수도 알 수 없다.
// scratch 도 자리를 유지한다. 기본값이라도 줄을 빼면 무엇을 고르는 자리인지 알 수 없다.
//
// 게이트는 두 축을 함께 판정한다. 한 섹션이 한 게이트라서, 어느 쪽이 부족해도 이 항목이 미완성이다.
function datasetsState(form: SubmitFormState): SectionState {
  const names = form.datasets.map((dataset) => dataset.label);
  const shown =
    names.length <= DATASET_NAMES_IN_SUMMARY
      ? names
      : [
          ...names.slice(0, DATASET_NAMES_IN_SUMMARY - 1),
          `외 ${names.length - (DATASET_NAMES_IN_SUMMARY - 1)}개`,
        ];
  return {
    summaryAxes: summaryOf([
      { name: "시작점", value: initSummary(form) ?? "처음부터 (scratch)" },
      { name: "Dataset", value: names.length === 0 ? EMPTY_AXIS : shown.join(" · ") },
    ]),
    status: hasTrainDataset(form.datasets) && isInitComplete(form) ? "ok" : "incomplete",
  };
}

// 한 줄에 이어 쓰는 Dataset 이름 수. 넘는 이름은 "외 N개" 로 표시한다.
const DATASET_NAMES_IN_SUMMARY = 2;

// scratch 는 기본값이라 줄을 쓰지 않는다(null). finetune·retrain 만 줄을 받는다.
function initSummary(form: SubmitFormState): string | null {
  const init = form.init;
  if (init.strategy === "scratch") {
    return null;
  }
  if (init.strategy === "retrain") {
    return `retrain · ${init.source.title}`;
  }
  return init.policy === null ? "finetune 미완료" : `finetune · ${init.policy.name}`;
}

// 브랜치와 config 는 서로 다른 축이라 한 줄씩 쓰고, 둘 다 고정 축이라 비어도 자리를 유지한다.
// 셋째 줄은 기본값에서 벗어난 값만 표시한다. 항상 붙는 조각은 바뀐 내용을 알려 주지 못한다.
function configState(form: SubmitFormState): SectionState {
  // 커밋을 고정했으면 Policy·Results 표와 같은 `Branch@Hash` 형식으로 한 줄에 적는다. 축을 하나 더
  // 추가하면 요약 줄 상한(3) 밖으로 변경 축이 밀린다. tip 은 모르므로 최신 추적은 브랜치 이름만 적는다.
  const ref = learningCoreRef(form);
  const branch = { name: "Branch", value: ref === "" ? EMPTY_AXIS : codeSourceLabel(form, null) };
  const config = {
    name: "config 파일",
    value: form.config.configName === "" ? EMPTY_AXIS : form.config.configName,
  };
  if (ref === "" || form.config.configName === "") {
    return { summaryAxes: [branch, config], status: "incomplete" };
  }
  // finetune 예약 키는 값이 있어도 BE 가 400 을 준다. 확정 스텝 전에 표시한다.
  const reserved =
    form.init.strategy === "finetune" ? reservedInitOverrideKeys(form.config.overrides).length : 0;
  if (reserved > 0) {
    return {
      summaryAxes: [branch, config, { name: "경고", value: `쓸 수 없는 키 ${reserved}개` }],
      status: "warning",
    };
  }
  const changed: string[] = [];
  const filled = form.config.overrides.filter((pair) => pair.key.trim() !== "").length;
  // 적용 방식은 배타다. 직접 편집이면 override 행이 비어 있으므로 건수 대신 방식을 적는다.
  if (isDirectConfigEdit(form.config)) {
    changed.push("본문 직접 편집");
  } else if (filled > 0) {
    changed.push(`override ${filled}건`);
  }
  if (!form.config.randomBackgroundEnabled) {
    changed.push("RandomBackground 제거");
  }
  return {
    summaryAxes: summaryOf([
      branch,
      config,
      { name: "변경", value: changed.length === 0 ? null : changed.join(" · ") },
    ]),
    status: isRecipeComplete(form) ? "ok" : "incomplete",
  };
}

// 시뮬레이션 데이터셋이 없어서 잠긴 Isaac Evaluation. 실행 축의 자리는 유지하되 값은 '사용 불가'다.
// 켜짐/꺼짐으로 쓰면 고를 수 있는 것처럼 보인다. 제출 게이트를 막지 않으므로 ok 다. 내비는 잠긴
// 항목에 준비 점을 그리지 않는다.
const LOCKED_EVALUATION_STATE: SectionState = {
  summaryAxes: [{ name: "실행", value: "사용 불가" }],
  status: "ok",
};

// 실행 여부가 고정 축이고, 켰을 때만 이미지·TASK 가 줄을 받는다. 꺼진 섹션에 빈 축을 두면
// 채워야 하는 것처럼 보인다. 켰는데 이미지·TASK·SEED 가 아직 없으면 미완성이다(구성 스텝의
// '다음' 게이트와 같은 판정).
function evaluationState(input: EvaluationSectionInput): SectionState {
  const run = { name: "실행", value: input.enabled ? "켜짐" : "꺼짐" };
  if (!input.enabled) {
    return { summaryAxes: [run], status: "ok" };
  }
  return {
    summaryAxes: [
      run,
      { name: "image", value: input.imageTag === "" ? EMPTY_AXIS : input.imageTag },
      { name: "TASK", value: input.task === "" ? EMPTY_AXIS : input.task },
    ],
    status: input.ready ? "ok" : "incomplete",
  };
}

// 1x1 잠금은 GPU 값의 사유라 값 옆에 붙인다. 별도 축으로 두면 무엇에 대한 설명인지 알기 어렵다.
function resourcesState(form: SubmitFormState): SectionState {
  const gpus = `${form.nodes} node × ${form.gpusPerNode} GPU`;
  return {
    summaryAxes: summaryOf([
      {
        name: "GPU",
        value: form.init.strategy === "finetune" ? `${gpus} (finetune 고정)` : gpus,
      },
      { name: "learning rate", value: form.autoLr ? "auto" : null },
    ]),
    status: isResourcesComplete(form) ? "ok" : "incomplete",
  };
}
